import type CreativeSDK from '@cesdk/cesdk-js';
import type { CreativeEngine } from '@cesdk/cesdk-js';
import { GQLProductFragment, GQLProductSideFragment } from '@theprintbar/graphql';
import {
	ImageSize,
	getDecorationPositionAsPercentage,
	getProductImageUrl,
	getSizedImageSourceSet,
} from '@theprintbar/shared';
import { getBackgroundId } from './block';
import { getPageSize } from './createPageForSide';

export function getAllPageBlocks(editor: CreativeSDK) {
	const pages = editor.engine.block.findByType('//ly.img.ubq/page');
	const blocks = pages.reduce((acc, page) => {
		const children = editor.engine.block.getChildren(page);
		return [...acc, ...children];
	}, [] as number[]);
	return blocks;
}

export function getPageScaleFactor(engine: CreativeEngine, page?: number) {
	let currentPage = page || engine.scene.getCurrentPage();
	if (!currentPage) return 1;
	return parseFloat(engine.block.getMetadata(currentPage, 'scaleFactor') || '1') || 1;
}

export function addBackgroundToPage(
	editor: CreativeSDK,
	webDomain: string,
	scene: number,
	page: number,
	product: GQLProductFragment,
	productSide: GQLProductSideFragment,
	colourCode?: string,
) {
	const { block } = editor.engine;
	const { imageOffsetLeft, imageOffsetTop, side } = productSide;

	const image = getProductImageUrl({
		product,
		productCode: product.code,
		colourCode,
		side,
		webDomain,
		size: ImageSize.ProductFull,
	});

	if (!image) throw `Couldnt find image for ${side} ${colourCode}`;

	const background = block.create('graphic');
	const rectShape = block.createShape('rect');
	const fill = block.createFill('image');
	let bgWidth = getPageSize(productSide).width;
	const bgDimension = bgWidth / (1 - (100 - productSide.imageWidth) / 100);

	block.setHeight(background, bgDimension);
	block.setWidth(background, bgDimension);

	block.setSourceSet(
		fill,
		'fill/image/sourceSet',
		getSizedImageSourceSet({
			//Pixel dimensions of the requested render, not the block's design units — the source set
			//describes the image, and the image is fetched at ProductFull above.
			url: image,
			originalWidth: ImageSize.ProductFull,
			originalHeight: ImageSize.ProductFull,
		}),
	);
	block.setFill(background, fill);
	block.setShape(background, rectShape);
	block.setKind(background, 'image');

	block.insertChild(scene, background, 0);

	block.setPositionX(background, block.getGlobalBoundingBoxX(page) + bgDimension * -(imageOffsetLeft / 100));
	block.setPositionY(background, block.getGlobalBoundingBoxY(page) + bgDimension * -(imageOffsetTop / 100));
	block.setScopeEnabled(background, 'editor/select', false);
	block.setName(background, getBackgroundId(side));
	block.setVisible(background, false);
	return background;
}

interface AddArtworkToPageProps {
	editor: CreativeSDK;
	page: number;
	artwork?: {
		width: number;
		height: number;
		url?: string | null;
	} | null;
	side?: GQLProductSideFragment;
	width?: number;
	height?: number;
	left?: number;
	top?: number;
}
export function addArtworkToPage({ editor, page, artwork, side, width, height, left, top }: AddArtworkToPageProps) {
	if (!artwork?.url) throw `Couldnt find image for design`;
	const { block } = editor.engine;

	const image = block.create('graphic');
	const rectShape = block.createShape('rect');
	const fill = block.createFill('image');

	//Add the image to the page
	block.setShape(image, rectShape);
	block.setKind(image, 'image');
	block.setSourceSet(
		fill,
		'fill/image/sourceSet',
		getSizedImageSourceSet({
			url: artwork.url,
			originalWidth: artwork.width,
			originalHeight: artwork.height,
		}),
	);

	block.setFill(image, fill);
	block.appendChild(page, image);

	//Now we figure out how to position it
	const pageWidth = block.getWidth(page);
	const pageHeight = block.getHeight(page);

	let imageHeight = artwork.height;
	let imageWidth = artwork.width;

	const imageRatio = imageWidth / imageHeight;
	const pageRatio = pageWidth / pageHeight;

	if (imageRatio > pageRatio) {
		imageWidth = pageWidth;
		imageHeight = imageWidth / imageRatio;
	} else {
		imageHeight = pageHeight;
		imageWidth = imageHeight * imageRatio;
	}

	const buffer = 0.9;
	block.setWidth(image, imageWidth * buffer);
	block.setHeight(image, imageHeight * buffer);

	block.alignHorizontally([image], 'Center');
	block.alignVertically([image], 'Center');

	const position = getDecorationPositionAsPercentage({
		width: width || 0,
		height: height || 0,
		left: left || 0,
		top: top || 0,
		centreOffset: 0,
		side,
	});

	if (left !== undefined) {
		block.setPositionX(image, block.getGlobalBoundingBoxX(page) + pageWidth * (position.left / 100));
	}

	if (top !== undefined) {
		block.setPositionY(image, block.getGlobalBoundingBoxY(page) + pageHeight * (position.top / 100));
	}

	if (width !== undefined) {
		block.setWidth(image, pageWidth * (position.width / 100));
	}

	if (height !== undefined) {
		block.setHeight(image, pageHeight * (position.height / 100));
	}

	return image;
}
